import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ProductCard } from "@/components/site/ProductCard";
import { Search } from "lucide-react";
import { useProducts, useCategories } from "@/lib/use-catalog";

export const Route = createFileRoute("/produkter/")({
  head: () => ({
    meta: [
      { title: "Alla produkter — Event Sverige" },
      { name: "description", content: "Bläddra i hela vårt sortiment: hoppborgar, tivolispel, scen, ljud och ljus. Lägg till i din förfrågan utan kostnad." },
      { property: "og:title", content: "Sortiment — Event Sverige" },
      { property: "og:description", content: "Bläddra i hela vårt sortiment: hoppborgar, tivolispel, scen, ljud och ljus." },
      { property: "og:url", content: "/produkter" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/produkter" }],
  }),
  component: ProductsPage,
});

function ProductsPage() {
  const [cat, setCat] = useState<string>("all");
  const [q, setQ] = useState("");
  const { data: products, isLoading } = useProducts();
  const { data: categories } = useCategories();

  const filtered = (products ?? []).filter((p) => {
    if (cat !== "all" && p.category_slug !== cat) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase()) && !p.short.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <span className="text-xs font-bold uppercase tracking-widest text-accent">Sortiment</span>
          <h1 className="mt-2 font-display text-5xl sm:text-6xl font-extrabold">Alla produkter</h1>
          <p className="mt-4 text-primary-foreground/80 max-w-2xl text-lg">
            Allt vi hyr ut, samlat på ett ställe. Inga priser visas online — vi sätter pris efter eventets längd, leveransort och paket.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-10">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCat("all")}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                cat === "all" ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
              }`}
            >Alla</button>
            {(categories ?? []).map((c) => (
              <button
                key={c.slug}
                onClick={() => setCat(c.slug)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  cat === c.slug ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                }`}
              >{c.name}</button>
            ))}
          </div>
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Sök produkt..."
              aria-label="Sök produkt"
              className="w-full rounded-full border border-border bg-card pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-card border border-border overflow-hidden">
                <div className="aspect-[4/3] bg-secondary/40 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-20 bg-secondary/60 rounded animate-pulse" />
                  <div className="h-5 w-3/4 bg-secondary/60 rounded animate-pulse" />
                  <div className="h-4 w-full bg-secondary/40 rounded animate-pulse" />
                  <div className="h-9 w-full bg-secondary/40 rounded-full animate-pulse mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-20">Inga produkter matchade din sökning.</p>
        ) : (
          <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filtered.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}
